/**
 * Google Tag Manager dataLayer adapter.
 *
 * Pushes events onto `window.dataLayer` so a GTM container can route them
 * to Google Ads / GA4 / etc. The shared `event_id` is included as a
 * dataLayer property so GA4 can match the server-side measurement
 * protocol fire from `spicychat-backend`.
 */

import type { EventEnvelope } from "@tracking/events";
import type { PixelAdapter, PixelEventMap } from "@tracking/pixels/types";

type WindowWithDataLayer = Window & {
  dataLayer?: Array<Record<string, unknown>>;
};

const GTM_EVENT_MAP: PixelEventMap = {
  funnel_landed: "page_view",
  funnel_step_viewed: "funnel_step_viewed",
  funnel_step_submitted: "funnel_step_submitted",
  funnel_step_skipped: "funnel_step_skipped",
  funnel_dropped: "funnel_dropped",
  funnel_qualified: "lead",
  funnel_account_created: "sign_up",
  funnel_handoff_clicked: "funnel_handoff_clicked",
  funnel_handoff_consumed: "funnel_handoff_consumed",
  funnel_paid_conversion: "purchase",
};

const ensureDataLayer = (): Array<Record<string, unknown>> | null => {
  if (typeof window === "undefined") return null;
  const win = window as WindowWithDataLayer;
  if (!Array.isArray(win.dataLayer)) {
    win.dataLayer = [];
  }
  return win.dataLayer;
};

export const fireGtmPixel = (envelope: EventEnvelope): void => {
  const dataLayer = ensureDataLayer();
  if (dataLayer === null) return;
  const eventName = GTM_EVENT_MAP[envelope.event.name];
  if (eventName === undefined) return;
  dataLayer.push({
    ...envelope.event.payload,
    event: eventName,
    event_id: envelope.event_id,
    onboarding_session_id: envelope.event.context.onboarding_session_id,
    funnel_id: envelope.event.context.funnel_id,
    override_id: envelope.event.context.override_id,
    application_id: envelope.event.context.application_id,
  });
};

export const gtmPixelAdapter: PixelAdapter = {
  platform: "gtm",
  fire: fireGtmPixel,
};

export const GTM_PIXEL_EVENT_MAP: PixelEventMap = GTM_EVENT_MAP;
